import Color from 'color';
import type { ReactNode } from 'react';

import {
  DroppableLastBlock,
  EmptyDocumentPlaceholder,
} from 'hb-builder/components/BuilderComponents';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { Wrapper, WrapperBuilder } from 'hb-builder/components/common/Wrapper';
import {
  useBuilderConfigContext,
  useComponentContext,
  useRendererContext,
  useBuilderContext,
  useDeviceModeContext,
} from 'hb-builder/hooks';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { BodyItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/bodyItemAttributes';

import BodySettings from './BodySettings';

const componentType = 'body';

const Body = () => <div />; // never used

interface RenderBuilderProps {
  children: ReactNode;
}

const RenderBuilder = ({ children }: RenderBuilderProps) => {
  const { item }: { item: DocumentItem } = useComponentContext();
  const { isMobileMode } = useDeviceModeContext();
  const {
    features: {
      body: { applyNetworkBgColor },
      color: { bgColor: bodyBackgroundColor },
    },
  } = useBuilderConfigContext();
  const { isDocumentEmpty } = useBuilderContext();

  const sanitizedAttributes = item?.attributes
    ? Object.entries(item?.attributes).reduce(
        (acc, [attributeKey, attributeValue]) => {
          if (
            !attributeValue ||
            (attributeKey === 'bodyBackgroundColor' &&
              Color(attributeValue).alpha() === 0)
          ) {
            return acc;
          }

          return { ...acc, [attributeKey]: attributeValue };
        },
        {},
      )
    : {};

  const attributes = {
    ...(applyNetworkBgColor && {
      bodyBackgroundColor,
    }),
    ...sanitizedAttributes,
  };

  return (
    // @ts-expect-error WrapperBuilder is not typed
    <WrapperBuilder
      dataTestId="body-wrapper-component"
      attributes={attributes}
      style={{
        background: attributes?.bodyBackgroundColor,
        backgroundAttachment: 'fixed',
        flexDirection: 'column',
        height: '100%',
        flex: isMobileMode ? undefined : 1,
        width: isMobileMode ? '375px' : undefined,
        overflow: isMobileMode ? 'hidden' : undefined,
        boxShadow: isMobileMode ? '0px 1px 6px 0px #0000001A' : undefined,
      }}
    >
      {!!isDocumentEmpty && <EmptyDocumentPlaceholder.RenderBuilder />}
      {children}
      {/*  Here we insert a "DroppableLastBlock" to create a space at the end of
        the document where new builder blocks can be inserted */}
      {!isDocumentEmpty && <DroppableLastBlock.RenderBuilder />}
    </WrapperBuilder>
  );
};

const Render = ({
  children,
  item,
}: RenderProps<DocumentItem<BodyItemAttributes>>) => {
  const { ids, isMobilePreview } = useRendererContext();
  const attributes = item?.attributes;

  return (
    // @ts-expect-error Wrapper is not typed
    <Wrapper
      attributes={attributes}
      componentType={componentType}
      isMobilePreview={isMobilePreview}
      style={{
        backgroundColor: attributes?.bodyBackgroundColor,
        display: 'block',
        height: 'auto',
        flex: 1,
        width: ids?.context?.includes('email') && attributes?.bodyWidth,
      }}
    >
      {children}
    </Wrapper>
  );
};

Body.componentName = 'Body';
Body.componentType = componentType;

Body.canBeDoubleClicked = false;
Body.canBeDragged = false;
Body.canBeListed = false;
Body.canBeSelected = false;

Body.item = {
  type: Body.componentName,
};

Body.Settings = BodySettings;

Body.RenderBuilder = RenderBuilder;
Body.Render = Render;

export default Body;
